"use client";

import React, { useState, useEffect } from "react";
import { Link, useParams, useNavigate, useLocation } from "react-router-dom";
import { motion, useScroll, useMotionValueEvent } from "framer-motion";
import { useTranslation } from "react-i18next";

import "./Navbar.css";
import logo1 from "../../assets/Logos/1/white.svg";
import logo2 from "../../assets/Logos/3/white.svg";
import globeIcon from "../../assets/globe.png";

const Navbar = () => {
  const { t, i18n } = useTranslation("global");
  const { scrollY } = useScroll();
  const { lang: currentLanguage } = useParams(); // ① language from URL
  const navigate = useNavigate();
  const location = useLocation();

  const [click, setClick] = useState(false);
  const [scroll, setScroll] = useState(false);
  const [languageDropdown, setLanguageDropdown] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [hidden, setHidden] = useState(false);

  const LANGS = ["en", "fr", "tr", "nl"]; // your supported codes

  // Scroll & hidden header logic
  useEffect(() => {
    window.addEventListener("scroll", () => setScroll(window.scrollY > 50));
    return () => window.removeEventListener("scroll", () => {});
  }, []);

  useMotionValueEvent(scrollY, "change", (latest) => {
    const prev = scrollY.getPrevious();
    setHidden(latest > prev && latest > 100);
  });

  // Mobile detection
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Lock body scroll on mobile nav open
  useEffect(() => {
    document.body.style.overflow = click && isMobile ? "hidden" : "auto";
  }, [click, isMobile]);

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  const closeMenu = () => setClick(false);
  const toggleLanguageDropdown = () => setLanguageDropdown(!languageDropdown);
  const handleClick = () => setClick(!click);

  // ② Change both i18next and URL
  const changeLanguage = (newLang) => {
    i18n.changeLanguage(newLang);
    setLanguageDropdown(false);

    // Build new path: replace old prefix with newLang
    const segments = location.pathname.split("/").filter(Boolean);
    // ['en','OurServices','SubPage'] or [] for home
    segments[0] = newLang;
    navigate("/" + segments.join("/"), { replace: true });
    scrollToTop();
  };

  // Render all other languages except current
  const renderLanguageOptions = () =>
    LANGS.filter((l) => l !== currentLanguage).map((l) => (
      <div
        key={l}
        className="language-option"
        onClick={() => changeLanguage(l)}
      >
        <h1>{l.toUpperCase()}</h1>
      </div>
    ));

  // Helper to prefix links
  const withLang = (path) => `/${currentLanguage}${path}`;

  return (
    <motion.div
      className={`header ${scroll ? "scrolled" : ""} ${
        isMobile ? "mobile" : ""
      }`}
      variants={{ visible: { y: 0 }, hidden: { y: "-100%" } }}
      animate={hidden ? "hidden" : "visible"}
      transition={{ duration: 0.2, ease: "easeInOut" }}
    >
      {isMobile ? (
        scroll ? (
          <>
            {/* Mobile hamburger */}
            <div className="mobile-menu-icon" onClick={handleClick}>
              <div className={`hamburger ${click ? "active" : ""}`}>
                <span />
                <span />
                <span />
              </div>
            </div>

            {/* Language switcher */}
            <div className="language-icon" onClick={toggleLanguageDropdown}>
              <img src={globeIcon} alt="language icon" />
              <div className="current-language">
                {currentLanguage.toUpperCase()}
              </div>
              <div
                className={`language-dropdown ${
                  languageDropdown ? "active" : ""
                }`}
              >
                {renderLanguageOptions()}
              </div>
            </div>

            {/* Mobile menu overlay */}
            {click && (
              <div className="mobile-menu-overlay">
                <ul className="nav-menu active" id="page-links">
                  <li className="mobile-only-link">
                    <Link
                      to={withLang("/")}
                      onClick={() => {
                        closeMenu();
                        scrollToTop();
                      }}
                    >
                      {t("navbar.home")}
                    </Link>
                  </li>
                  <li>
                    <Link
                      to={withLang("/OurServices")}
                      onClick={() => {
                        closeMenu();
                        scrollToTop();
                      }}
                    >
                      {t("navbar.services")}
                    </Link>
                  </li>
                  <li>
                    <Link
                      to={withLang("/AboutUs")}
                      onClick={() => {
                        closeMenu();
                        scrollToTop();
                      }}
                    >
                      {t("navbar.aboutUs")}
                    </Link>
                  </li>
                  <li>
                    <Link
                      to={withLang("/Pricing")}
                      onClick={() => {
                        closeMenu();
                        scrollToTop();
                      }}
                    >
                      {t("navbar.pricing")}
                    </Link>
                  </li>
                  <li>
                    <Link
                      to={withLang("/ContactUs")}
                      onClick={() => {
                        closeMenu();
                        scrollToTop();
                      }}
                    >
                      {t("navbar.contact")}
                    </Link>
                  </li>
                </ul>
              </div>
            )}
          </>
        ) : (
          // Mobile pre-scroll: just logo
          <Link
            to={withLang("/")}
            className="header-title"
            onClick={scrollToTop}
          >
            <img src={logo1} alt="logo" className="logo" />
          </Link>
        )
      ) : (
        // Desktop header
        <>
          <Link
            to={withLang("/")}
            className="header-title"
            onClick={scrollToTop}
          >
            <img src={scroll ? logo2 : logo1} alt="logo" className="logo" />
          </Link>

          <ul
            className={click ? "nav-menu active" : "nav-menu"}
            id="page-links"
          >
            <li>
              <Link
                to={withLang("/OurServices")}
                onClick={() => {
                  closeMenu();
                  scrollToTop();
                }}
              >
                {t("navbar.services")}
              </Link>
            </li>
            <li>
              <Link
                to={withLang("/AboutUs")}
                onClick={() => {
                  closeMenu();
                  scrollToTop();
                }}
              >
                {t("navbar.aboutUs")}
              </Link>
            </li>
            <li>
              <Link
                to={withLang("/Pricing")}
                onClick={() => {
                  closeMenu();
                  scrollToTop();
                }}
              >
                {t("navbar.pricing")}
              </Link>
            </li>
            <li>
              <Link
                to={withLang("/ContactUs")}
                onClick={() => {
                  closeMenu();
                  scrollToTop();
                }}
              >
                {t("navbar.contact")}
              </Link>
            </li>
          </ul>

          <div className="language-icon" onClick={toggleLanguageDropdown}>
            <img src={globeIcon} alt="language icon" />
            <div className="current-language">
              {currentLanguage.toUpperCase()}
            </div>
            <div
              className={`language-dropdown ${
                languageDropdown ? "active" : ""
              }`}
            >
              {renderLanguageOptions()}
            </div>
          </div>
        </>
      )}
    </motion.div>
  );
};

export default Navbar;
